import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const SITE_URL = 'https://offshoreadvisory.pro';

/**
 * SEO head manager.
 *
 * react-helmet-async is not reflecting tags into <head> in this app, so the
 * document head is managed imperatively here. Every tag this component owns is
 * marked with `data-seo` and rewritten on each render, so switching pages (or
 * language) never leaves stale or duplicated meta behind.
 *
 * The `url` prop is the language-neutral path (e.g. "/banking-license"); the
 * English and Chinese (/zh) variants — and reciprocal hreflang alternates — are
 * derived from it and the active i18n language.
 */
export const SEO = ({
    title,
    description,
    keywords,
    author = 'Offshore Advisory',
    url,
    type = 'website',
    robots = 'index, follow',
}) => {
    const { i18n } = useTranslation();
    const lang = (i18n.language || 'en').startsWith('zh') ? 'zh' : 'en';

    useEffect(() => {
        const path = url || '/';
        const enUrl = path === '/' ? `${SITE_URL}/en` : `${SITE_URL}/en${path}`;
        const zhUrl = path === '/' ? `${SITE_URL}/zh` : `${SITE_URL}/zh${path}`;
        const currentUrl = lang === 'zh' ? zhUrl : enUrl;
        const ogLocale = lang === 'zh' ? 'zh_CN' : 'en_US';
        const altLocale = lang === 'zh' ? 'en_US' : 'zh_CN';

        const head = document.head;
        // Remove tags this component previously created.
        head.querySelectorAll('[data-seo]').forEach((n) => n.remove());

        const add = (tag, attrs) => {
            const el = document.createElement(tag);
            el.setAttribute('data-seo', '');
            Object.entries(attrs).forEach(([k, v]) => {
                if (v != null) el.setAttribute(k, v);
            });
            head.appendChild(el);
        };

        if (title) document.title = title;

        // Primary meta
        if (title) add('meta', { name: 'title', content: title });
        if (description) add('meta', { name: 'description', content: description });
        if (keywords) add('meta', { name: 'keywords', content: keywords });
        if (author) add('meta', { name: 'author', content: author });
        add('meta', { name: 'robots', content: robots });

        // Open Graph
        add('meta', { property: 'og:type', content: type });
        add('meta', { property: 'og:url', content: currentUrl });
        if (title) add('meta', { property: 'og:title', content: title });
        if (description) add('meta', { property: 'og:description', content: description });
        add('meta', { property: 'og:locale', content: ogLocale });
        add('meta', { property: 'og:locale:alternate', content: altLocale });

        // Twitter
        add('meta', { property: 'twitter:card', content: 'summary_large_image' });
        add('meta', { property: 'twitter:url', content: currentUrl });
        if (title) add('meta', { property: 'twitter:title', content: title });
        if (description) add('meta', { property: 'twitter:description', content: description });

        // Canonical + hreflang alternates
        add('link', { rel: 'canonical', href: currentUrl });
        add('link', { rel: 'alternate', hreflang: 'en', href: enUrl });
        add('link', { rel: 'alternate', hreflang: 'zh-CN', href: zhUrl });
        add('link', { rel: 'alternate', hreflang: 'x-default', href: enUrl });
    }, [title, description, keywords, author, url, type, robots, lang]);

    return null;
};

export default SEO;
